/**
 * Service API organisations publiques — version donoor.org
 *
 * Couche 1 — Transport (ADR-001). Endpoints publics utilisés par la page
 * association `/associations/[slug]` pour paginer les sections (campagnes,
 * événements, adhésions, posts) côté client après hydratation.
 *
 * La page elle-même utilise `useAsyncData` SSR pour récupérer le payload
 * initial (SEO-critique). Ce service couvre la pagination CSR post-hydration
 * déclenchée par l'utilisateur dans la modal "Voir tout", ET les pages
 * « tout ce que l'association propose » (`/associations/[slug]/<domaine>`,
 * cf. `AssoListeFeature`) — rendues côté serveur.
 */

import { apiCall } from "@donoor/data-layer";

const PUBLIC_OPTS = { skipAuth: true, skipOrganizationId: true } as const;

// Sections paginables côté UI. Notez que `memberships` est traduit en
// `membership-plans` côté URL (compat backend).
export type OrganizationSection = "campaigns" | "events" | "memberships" | "posts";

// Les items renvoyés ont des shapes hétérogènes (campaign vs event vs
// membership-plan vs post). On garde un type indexable pour le rendu
// dynamique dans le template.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type OrganizationSectionItem = Record<string, any>;

export interface OrganizationSectionPage {
  success: boolean;
  data: OrganizationSectionItem[];
  pagination: { hasMore: boolean };
}

function sectionToEndpoint(section: OrganizationSection): string {
  return section === "memberships" ? "membership-plans" : section;
}

export const organizationsApi = {
  /**
   * Récupère une page d'items pour une section donnée d'une organisation
   * publique (modal "Voir tout"). Pagination par offset + limit.
   */
  section(
    slug: string,
    section: OrganizationSection,
    params: { offset: number; limit: number },
    signal?: AbortSignal,
  ): Promise<OrganizationSectionPage> {
    const endpoint = sectionToEndpoint(section);
    return apiCall<OrganizationSectionPage>(
      `/api/organizations/public/slug/${slug}/${endpoint}`,
      {
        query: { offset: params.offset, limit: params.limit },
        signal,
        ...PUBLIC_OPTS,
      },
    );
  },
};

// ─── Pages « tout ce que l'association propose » (AssoListeFeature) ──────

/** Domaines listables, tels qu'ils apparaissent dans l'URL de l'API. */
export type DomaineAsso =
  | "campaigns"
  | "events"
  | "membership-plans"
  | "shops"
  | "qr-codes";

export interface PageListeDomaine {
  data: OrganizationSectionItem[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
}

/**
 * ⚠️ `$fetch` + URL de base EXPLICITE (et non `apiCall`) : ces lectures
 * tournent aussi côté SERVEUR (useAsyncData / `suspense()` SSR). La base est
 * résolue par l'appelant au setup — un `useRuntimeConfig()` appelé depuis une
 * `queryFn` asynchrone côté serveur n'aurait plus de contexte Nuxt.
 */
export const associationListesApi = {
  /**
   * En-tête de l'association (nom, logo, bannière). `null` si introuvable ou
   * en erreur : l'appelant en fait une 404 franche.
   */
  async entete(
    baseUrl: string,
    slug: string,
  ): Promise<OrganizationSectionItem | null> {
    const res = await $fetch<{ success: boolean; data: OrganizationSectionItem }>(
      `${baseUrl}/api/organizations/public/slug/${slug}`,
    ).catch(() => null);
    return res?.success ? res.data : null;
  },

  /** Une page (offset + limit) des éléments publiés d'un domaine. */
  async page(
    baseUrl: string,
    slug: string,
    domaine: DomaineAsso,
    params: { offset: number; limit: number },
    signal?: AbortSignal,
  ): Promise<PageListeDomaine> {
    const res = await $fetch<{ success: boolean } & PageListeDomaine>(
      `${baseUrl}/api/organizations/public/slug/${slug}/${domaine}`,
      { query: { offset: params.offset, limit: params.limit }, signal },
    );
    return { data: res.data ?? [], pagination: res.pagination };
  },
};
